import { useEffect, useMemo, useState } from 'react'
import { OrdinalsFetchError } from '../clients/OrdinalsClient'
import { InsufficientSpendableError, UTXOFetchError } from '../clients/TatumClient'
import { UTXO_CHAINS } from '../constants/business'
import { Chain, EVMChain, PlugToken } from '../interfaces/common'
import { UTXOChain } from '../interfaces/walletActions'
import { logger } from '../logger'
import { addMultiplier } from '../utils/bigint'
import { asEvmToken } from '../utils/castToken'
import { executeAndSetInterval } from '../utils/executeAndSetInterval'
import { GasCalculator } from '../utils/GasCalculator'
import { getEthProvider } from '../utils/getEthProvider'
import { getWalletAddress } from '../utils/getWalletAddress'
import { decimalsToE8s } from '../utils/validationHelper'
import useWallet from './useWallet'

export type UtxoFeeError =
  | { kind: 'networkUnavailable' }
  | { kind: 'inscriptionsUnavailable' }
  | { kind: 'insufficientSpendable'; spendable: bigint; locked: bigint; required: bigint }
  | { kind: 'unknown' }

const isUtxoChain = (chain: Chain | undefined) => {
  return chain !== undefined && UTXO_CHAINS.includes(chain as UTXOChain)
}

export const ETH_TRANSFER_GAS = 21000n
export const ERC_20_DEPOSIT_GAS = 100000n

function refetchValue<ReturnType>(args: any[], callback: () => Promise<ReturnType>) {
  const [value, setValue] = useState<ReturnType>()

  useEffect(() => {
    setValue(undefined)

    let clearFetch: Function
    const timeout = setTimeout(() => {
      clearFetch = executeAndSetInterval(async () => {
        try {
          const estimate = await callback()

          setValue(estimate)
        } catch (e) {
          if (!(e as any).reason?.includes('amount exceeds balance')) {
            logger.error(e as Error)
          }
        }
      }, 10000)
    }, 1000)

    return () => {
      clearFetch?.()
      clearTimeout(timeout)
    }
  }, args)

  return value
}

export const useETHGasFee = (
  isEnabled: boolean,
  chain: EVMChain | undefined,
  fromAddress: string | undefined,
  transferAmount: bigint | undefined
) => {
  const provider = useMemo(() => chain && getEthProvider(chain.rpcEndpoint), [chain])
  const gasCalculator = useMemo(() => chain && new GasCalculator(chain), [chain])

  const defaultValue = {
    failed: false,
    gasLimit: undefined,
    maxFeePerGas: undefined,
    maxPriorityFeePerGas: undefined,
  }

  const value = refetchValue([isEnabled, chain, transferAmount, fromAddress], async () => {
    if (!isEnabled || !provider || !gasCalculator) {
      return
    }

    return gasCalculator.getEthGas(fromAddress, transferAmount)
  })

  return value || defaultValue
}

export const useErc20ApproveGas = (
  isEnabled: boolean,
  tokenAddress: string | undefined,
  transferAmount: number | undefined,
  chain?: EVMChain,
  contractAddress?: string
) => {
  const { wallet } = useWallet()
  const gasCalculator = useMemo(() => new GasCalculator(chain), [chain])

  return refetchValue(
    [wallet.ethAddress, tokenAddress, transferAmount, contractAddress],
    async () => {
      if (
        !isEnabled ||
        !wallet.ethAddress ||
        !tokenAddress ||
        !contractAddress ||
        !transferAmount
      ) {
        return
      }

      const estimate = await gasCalculator.estimateErc20ApproveGas(
        wallet.ethAddress,
        tokenAddress,
        transferAmount,
        contractAddress
      )

      return estimate && addMultiplier(estimate, 1.3)
    }
  )
}

export const useEthDepositGas = (isEnabled: boolean, chain?: EVMChain) => {
  const { wallet } = useWallet()
  const gasCalculator = useMemo(() => new GasCalculator(chain), [chain])

  return refetchValue([isEnabled, wallet.principalId], async () => {
    if (!isEnabled) return

    const estimate = await gasCalculator.estimateEthDepositGas(wallet.principalId)

    return estimate
  })
}

export const useTransferFeeEstimate = (
  token: PlugToken | undefined,
  inputAmount: string,
  toAddress?: string,
  swapTargetToken?: PlugToken
) => {
  const [solFee, setSolFee] = useState<bigint | undefined>()
  const [ethFee, setEthFee] = useState<bigint | undefined>()
  const [utxoFee, setUtxoFee] = useState<bigint | undefined>()
  const [utxoFeeError, setUtxoFeeError] = useState<UtxoFeeError | undefined>()
  const [isLoading, setIsLoading] = useState(false)

  const {
    wallet: { btcAddress, ethAddress, solAddress, dogeAddress },
  } = useWallet()
  const amount = token ? BigInt(decimalsToE8s(inputAmount, token.decimals)) : undefined

  const calculator = useMemo(() => new GasCalculator(token?.evmChain), [token?.evmChain])

  const utxoAddress = useMemo(() => {
    if (token?.chain === Chain.BTC) return btcAddress
    if (token?.chain === Chain.DOGE) return dogeAddress
    return undefined
  }, [token?.chain, btcAddress, dogeAddress])

  useEffect(() => {
    if (!solAddress || token?.chain !== Chain.SOL) {
      setSolFee(undefined)
    } else {
      setIsLoading(true)
      calculator
        .estimateSolTransferFee(swapTargetToken || token, toAddress || solAddress)
        .then(setSolFee)
        .catch(() => setSolFee(undefined))
        .finally(() => setIsLoading(false))
    }
  }, [solAddress, token, swapTargetToken, toAddress])

  useEffect(() => {
    if (!ethAddress || !token?.evmChain || !amount) {
      setEthFee(undefined)
    } else {
      setIsLoading(true)
      calculator
        .estimateEthTransferFee(asEvmToken(token), ethAddress, amount)
        .then(setEthFee)
        .catch(() => setEthFee(undefined))
        .finally(() => setIsLoading(false))
    }
  }, [ethAddress, amount, token])

  useEffect(() => {
    if (!token || !utxoAddress || !isUtxoChain(token?.chain) || !amount) {
      setUtxoFee(undefined)
      setUtxoFeeError(undefined)
    } else {
      setIsLoading(true)
      setUtxoFeeError(undefined)
      calculator
        .estimateUTXOTransferFee(utxoAddress, amount, token.chain as UTXOChain)
        .then((fee) => {
          setUtxoFee(fee)
        })
        .catch((e) => {
          setUtxoFee(undefined)
          if (e instanceof InsufficientSpendableError) {
            setUtxoFeeError({
              kind: 'insufficientSpendable',
              spendable: e.spendable,
              locked: e.lockedInInscriptions,
              required: e.required,
            })
          } else if (e instanceof UTXOFetchError) {
            setUtxoFeeError({ kind: 'networkUnavailable' })
          } else if (e instanceof OrdinalsFetchError) {
            setUtxoFeeError({ kind: 'inscriptionsUnavailable' })
          } else {
            logger.debug('Unexpected UTXO fee estimation error', { originalError: e })
            setUtxoFeeError({ kind: 'unknown' })
          }
        })
        .finally(() => setIsLoading(false))
    }
  }, [utxoAddress, amount, token])

  return { solFee, ethFee, utxoFee, utxoFeeError, isLoading }
}

export const useMaxUtxoSendable = (token: PlugToken | undefined) => {
  const [maxAmount, setMaxAmount] = useState<bigint | undefined>()
  const { wallet } = useWallet()
  const address = isUtxoChain(token?.chain) ? getWalletAddress(token, wallet) : undefined

  useEffect(() => {
    setMaxAmount(undefined)

    if (!address || !isUtxoChain(token?.chain)) return

    let cancelled = false
    new GasCalculator()
      .estimateMaxUTXOSendable(address, token!.chain as UTXOChain)
      .then(({ maxAmount }) => {
        if (!cancelled) setMaxAmount(maxAmount)
      })
      .catch((e) => {
        logger.debug('Failed to estimate max UTXO sendable', { originalError: e })
        if (!cancelled) setMaxAmount(undefined)
      })

    return () => {
      cancelled = true
    }
  }, [address, token?.chain])

  return maxAmount
}
